import React from 'react';
import useSMSSettings from '../hooks/useSMSSettings';
import ReminderControls from './ReminderControls';
import NotificationPreferences from './NotificationPreferences';
import SimpleSMSTemplateEditor from './simple/SimpleSMSTemplateEditor';
import { defaultTemplates } from '../constants/defaultTemplates';
import { isPendingConfirmationReplyOnlyEnabled } from '../../../config/featureFlags';
import './SMSSettingsPanel.css';

const TEMPLATE_OPTIONS = [
  {
    key: 'bookingConfirmation',
    title: 'Booking confirmation',
    description: 'Sent when an appointment is confirmed.',
    mergeFields: ['customerName', 'service', 'date', 'time']
  },
  {
    key: 'scheduleChange',
    title: 'Schedule change',
    description: 'Sent when an appointment is rescheduled.',
    mergeFields: ['customerName', 'service', 'oldDate', 'oldTime', 'newDate', 'newTime']
  },
  {
    key: 'pendingConfirmationRequest',
    title: 'Client confirmation',
    description: isPendingConfirmationReplyOnlyEnabled()
      ? 'Sent when staff requests client attendance confirmation. Clients reply CONFIRM by SMS.'
      : 'Sent when staff requests client attendance confirmation.',
    mergeFields: isPendingConfirmationReplyOnlyEnabled()
      ? ['customerName', 'service', 'date', 'time']
      : ['customerName', 'service', 'date', 'time', 'confirmUrl']
  },
  {
    key: 'checkInLinkRequest',
    title: 'Check-in link',
    description: 'Sent automatically before appointments when check-in reminders are enabled in booking settings.',
    mergeFields: ['customerName', 'service', 'date', 'time', 'checkInUrl']
  }
];

const REMINDER_TEMPLATE = {
  key: 'appointmentReminder',
  title: 'Reminder text',
  description: 'The SMS clients receive before their appointment.',
  mergeFields: ['customerName', 'service', 'time']
};

const SMSSettingsPanel = ({ companyId, companyData }) => {
  const [activeTemplateKey, setActiveTemplateKey] = React.useState(TEMPLATE_OPTIONS[0].key);
  const {
    settings,
    loading,
    error,
    saving,
    updateSettings,
    sendTestSMS
  } = useSMSSettings(companyId);

  if (loading && !settings) {
    return (
      <div className="sms-settings-loading">
        <div className="sms-settings-spinner"></div>
      </div>
    );
  }

  if (error && !settings) {
    return <div className="sms-settings-error">{error}</div>;
  }

  const isTrial = Boolean(companyData?.package?.subscription?.status === 'trial');
  const activeTemplate = TEMPLATE_OPTIONS.find((option) => option.key === activeTemplateKey) || TEMPLATE_OPTIONS[0];

  const handleSettingsUpdate = (newSettings) => {
    updateSettings(newSettings);
  };

  const updateTemplate = (key, template) => {
    handleSettingsUpdate({
      ...settings,
      templates: {
        ...settings.templates,
        [key]: template
      }
    });
  };

  return (
    <div className="sms-settings-panel">
      <div className="sms-settings-container">
        <div className="sms-settings-page-header">
          <div>
            <h1 className="sms-settings-page-title">SMS settings</h1>
            <p className="sms-settings-page-description">
              Manage SMS preferences, edit templates, and adjust reminders from one simple page.
            </p>
          </div>
        </div>

        {error && settings && <div className="sms-settings-error">{error}</div>}

        <div className="sms-settings-content">
          <section className="sms-settings-section-card">
            <div className="sms-settings-section-header">
              <h2 className="sms-settings-section-title">Preferences</h2>
              <p className="sms-settings-section-description">
                Choose which SMS notifications are enabled for your clients.
              </p>
            </div>
            <NotificationPreferences
              settings={settings}
              onUpdate={handleSettingsUpdate}
              isTrial={isTrial}
            />
          </section>

          <section className="sms-settings-section-card">
            <div className="sms-settings-section-header">
              <h2 className="sms-settings-section-title">Templates</h2>
              <p className="sms-settings-section-description">
                Pick a template, edit the message, then apply your changes.
              </p>
            </div>

            <div className="sms-settings-template-layout">
              <div className="sms-settings-template-list" role="tablist" aria-label="SMS templates">
                {TEMPLATE_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`sms-settings-template-tab ${activeTemplateKey === option.key ? 'active' : ''}`}
                    onClick={() => setActiveTemplateKey(option.key)}
                  >
                    <span>{option.title}</span>
                    <small>{option.description}</small>
                  </button>
                ))}
              </div>

              <div className="sms-settings-editor-card">
                <SimpleSMSTemplateEditor
                  title={activeTemplate.title}
                  description={activeTemplate.description}
                  template={
                    settings?.templates?.[activeTemplate.key] ||
                    defaultTemplates.templates[activeTemplate.key]
                  }
                  mergeFields={activeTemplate.mergeFields}
                  onApply={(template) => updateTemplate(activeTemplate.key, template)}
                  onTestSMS={(phoneNumber, customMessage) => sendTestSMS(activeTemplate.key, phoneNumber, customMessage)}
                  saving={saving}
                />
              </div>
            </div>
          </section>

          <section className="sms-settings-section-card">
            <div className="sms-settings-section-header">
              <h2 className="sms-settings-section-title">Reminders</h2>
              <p className="sms-settings-section-description">
                Keep reminder timing and the reminder message together in one place.
              </p>
            </div>

            <div className="sms-settings-reminder-grid">
              <div className="sms-settings-editor-card">
                <ReminderControls
                  enabled={settings?.reminderEnabled}
                  reminders={settings?.reminders || []}
                  settings={settings}
                  isTrial={isTrial}
                  onUpdate={(reminderSettings) => {
                    handleSettingsUpdate({
                      ...settings,
                      ...reminderSettings
                    });
                  }}
                />
              </div>

              <div className="sms-settings-editor-card">
                <SimpleSMSTemplateEditor
                  title={REMINDER_TEMPLATE.title}
                  description={REMINDER_TEMPLATE.description}
                  template={settings?.templates?.appointmentReminder}
                  mergeFields={REMINDER_TEMPLATE.mergeFields}
                  onApply={(template) => updateTemplate('appointmentReminder', template)}
                  onTestSMS={(phoneNumber, customMessage) => sendTestSMS('appointmentReminder', phoneNumber, customMessage)}
                  saving={saving}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SMSSettingsPanel;